/**
 * Modern Bold · <product-form> custom element
 * ─────────────────────────────────────────────────────────────────────
 * Wraps any product form (Shopify-rendered {% form 'product' %} or a
 * hand-written one) and turns its submit into an AJAX add-to-cart with
 * full state UX. Place it anywhere — main product detail, mobile sticky CTA,
 * quick-add modal, cart drawer cross-sell — as long as the host contains
 * a <form> with `name="id"` and `name="quantity"` inputs, it works.
 *
 * Why a fresh implementation? Dawn's product-form.js is tightly coupled
 * to the cart-notification / Section Rendering API helpers from
 * global.js and assumes a `.loading__spinner` markup contract. We need
 * something self-contained that the modern PDP, sticky CTA, and any
 * future surface (quick-add, drawer cross-sell) can rely on with a
 * stable data-attribute + event contract.
 *
 * ─── DATA CONTRACT ───────────────────────────────────────────────────
 *   <product-form
 *     data-section-id="..."          optional; surfaced in events
 *     data-cart-sections="cart-drawer,cart-icon-bubble"
 *                                    sections returned by the add-cart
 *                                    response and used for inline render
 *     data-success-label="Added ✓"   transient text after a successful add
 *     data-success-duration="1400"   ms before success → idle
 *     data-loading-label="Adding…"   text while the request is in flight
 *     data-hide-errors="true"        suppresses inline error UI
 *   >
 *     <form id="...">
 *       <input name="id"       value="..."> <!-- variant id -->
 *       <input name="quantity" value="1">
 *       …
 *       <button type="submit" name="add" data-mb-add>
 *         <span data-mb-add-label>Add to cart</span>
 *       </button>
 *     </form>
 *     <div data-mb-error hidden role="alert"></div>
 *   </product-form>
 *
 * External CTAs (e.g. a mobile sticky bar rendered outside the host)
 * participate via the standard HTML `form="<formId>"` attribute. Their
 * label is mirrored if they contain [data-mb-add-label]; otherwise the
 * button's first text node is replaced (icon children stay intact).
 *
 * ─── STATES (data-state on host) ─────────────────────────────────────
 *   idle         default — ready to add
 *   loading      request in flight
 *   added        transient post-success state
 *   error        last submit failed (auto-clears on any form input)
 *   sold-out     external code may set this; we honour the disabled attr
 *   unavailable  combination doesn't resolve to a real variant
 *
 * Variant-picker logic outside this file owns the native `disabled`
 * attribute on the CTA — we only own the loading / added / error flow,
 * and we mirror `disabled` into `aria-disabled` for visual styling.
 *
 * ─── EVENTS (bubbling, composed CustomEvents) ────────────────────────
 *   product-form:loading      → { variantId, sectionId }
 *   product-form:added        → { variantId, sectionId, item, response }
 *   product-form:error        → { variantId, sectionId, message, errors }
 *   product-form:state-change → { state, prev }
 *
 * Also publishes to Dawn's PUB_SUB_EVENTS.cartUpdate / cartError when
 * those globals are present so third-party Dawn extensions keep working.
 *
 * ─── NETWORK ────────────────────────────────────────────────────────
 *   POST `${routes.cart_add_url || '/cart/add'}.js`
 *   - JSON body: { items: [main, ...bundle], sections, sections_url }
 *   - Main item is built from the form: { id, quantity, properties?,
 *     selling_plan? }. line-item `properties[*]` inputs are collected
 *     into the properties object.
 *   - If a sibling <product-bundle> exists on the page, its
 *     getSelectedItems() output (selected pad + addons) is concatenated
 *     so the cart addition is ATOMIC — Shopify either accepts the whole
 *     bundle or rejects it and nothing lands. No second request, no
 *     race, no half-bundles.
 *
 * No external dependencies. ES2017+. Loaded only on pages that contain
 * a product form; the customElements guard makes it idempotent.
 */
(function () {
  'use strict';

  // Belt-and-braces: if Dawn's product-form.js already registered the
  // element (e.g. the old main-product.liquid section is still on the
  // page), don't redefine — let Dawn drive that surface.
  if (customElements.get('product-form')) return;

  const DEFAULT_SECTIONS = 'cart-drawer,cart-icon-bubble';
  const SUCCESS_DURATION_MS = 1400;

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this._submitting = false;
      this._successTimer = null;
      this._onSubmit = this._onSubmit.bind(this);
      this._onFormMutation = this._onFormMutation.bind(this);
      this._clearError = this._clearError.bind(this);
    }

    connectedCallback() {
      // The form may be rendered after connectedCallback in rare cases
      // (lazy section render). Defer one tick if it's not here yet.
      this.form = this.querySelector('form');
      if (!this.form) {
        queueMicrotask(() => this.connectedCallback());
        return;
      }
      if (this.form.dataset.pfBound === '1') return;
      this.form.dataset.pfBound = '1';

      // Native validation gets in the way of the AJAX flow.
      this.form.setAttribute('novalidate', 'novalidate');

      this.variantInput = this.form.querySelector('[name=id]');
      this.cart =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification') ||
        null;
      this.errorRegion = this.querySelector('[data-mb-error]');

      this.dataset.state = this.dataset.state || 'idle';

      this.form.addEventListener('submit', this._onSubmit);
      this.form.addEventListener('change', this._clearError);
      this.form.addEventListener('input', this._clearError);

      // External submit buttons (sticky CTA below the fold etc.) attach
      // to this form via `form="<id>"`. Tag them with aria-haspopup so
      // AT users know the click opens the cart drawer dialog.
      if (this.cart && this.cart.tagName === 'CART-DRAWER') {
        this._getSubmitButtons().forEach((btn) =>
          btn.setAttribute('aria-haspopup', 'dialog')
        );
      }
    }

    disconnectedCallback() {
      if (this._successTimer) clearTimeout(this._successTimer);
    }

    // ── State ───────────────────────────────────────────────────────

    _setState(next) {
      const prev = this.dataset.state || 'idle';
      if (prev === next) return;
      this.dataset.state = next;
      this._reflectButtons(next);
      this.dispatchEvent(
        new CustomEvent('product-form:state-change', {
          bubbles: true,
          composed: true,
          detail: { state: next, prev },
        })
      );
    }

    /**
     * Collect every submit button bound to this form — those nested
     * inside it plus those declared elsewhere via the HTML `form` attr.
     */
    _getSubmitButtons() {
      const inner = Array.from(
        this.form.querySelectorAll('button[type=submit], input[type=submit]')
      );
      if (!this.form.id) return inner;
      const sel = '[form="' + (window.CSS && CSS.escape ? CSS.escape(this.form.id) : this.form.id) + '"]';
      const external = Array.from(document.querySelectorAll(sel)).filter(
        (el) =>
          !this.form.contains(el) &&
          (el.type === 'submit' || el.tagName === 'BUTTON')
      );
      return inner.concat(external);
    }

    /**
     * Build a Shopify /cart/add.js JSON `items[]` entry from the form.
     * Picks up id (variantId is already resolved by the caller), quantity
     * (defaulting to 1), any line-item properties[X] inputs, and an
     * optional selling_plan. Skips empty property values so the cart
     * doesn't get noisy { "key": "" } pairs.
     */
    _buildItemFromForm(variantId) {
      const qtyInput = this.form.querySelector('[name=quantity]');
      const qty = parseInt(qtyInput && qtyInput.value, 10);
      const item = {
        id: parseInt(variantId, 10),
        quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
      };

      const properties = {};
      this.form.querySelectorAll('[name^="properties["]').forEach((el) => {
        const m = el.name.match(/^properties\[(.+)\]$/);
        if (!m) return;
        // Checkboxes only submit when checked; skip the rest.
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        if (el.value === '' || el.value == null) return;
        properties[m[1]] = el.value;
      });
      if (Object.keys(properties).length) item.properties = properties;

      const sp = this.form.querySelector('[name=selling_plan]');
      const spInt = sp && parseInt(sp.value, 10);
      if (Number.isFinite(spInt) && spInt > 0) item.selling_plan = spInt;

      return item;
    }

    _setButtonText(btn, txt) {
      const slot = btn.querySelector('[data-mb-add-label]');
      if (slot) {
        slot.textContent = txt;
        return;
      }
      // Sticky-CTA style: no inner span; replace the first text node and
      // leave any sibling <svg>/<span> icons alone.
      for (const node of btn.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()) {
          node.nodeValue = txt;
          return;
        }
      }
      btn.insertBefore(document.createTextNode(txt), btn.firstChild);
    }

    /**
     * Apply the visual state to every submit button. We do NOT touch
     * the native `disabled` attribute — that's the variant picker's
     * source of truth for sold-out. We only mirror it into aria-disabled
     * for CSS hooks, and we own the busy/added text swap.
     */
    _reflectButtons(state) {
      const btns = this._getSubmitButtons();
      btns.forEach((btn) => {
        // Visual classes — kept simple so CSS can hit both the product
        // detail CTA and external sticky-CTA via the same selectors,
        // without any parent-state coupling.
        btn.classList.toggle('is-loading', state === 'loading');
        btn.classList.toggle('is-added', state === 'added');
        btn.classList.toggle('is-error', state === 'error');

        // aria-disabled mirrors native disabled or busy state.
        if (state === 'loading') {
          btn.setAttribute('aria-disabled', 'true');
        } else if (btn.disabled) {
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.removeAttribute('aria-disabled');
        }

        if (state === 'loading') {
          this._setButtonText(btn, this.dataset.loadingLabel || 'Adding…');
        } else if (state === 'added') {
          this._setButtonText(btn, this.dataset.successLabel || 'Added ✓');
        } else {
          // idle / error / sold-out / unavailable — restore the label
          // by reading the current native disabled state. We avoid
          // caching "prev label" because the variant picker may have
          // rewritten it during loading (e.g. switched to sold-out).
          this._setButtonText(btn, btn.disabled ? 'Sold out' : 'Add to cart');
        }
      });
    }

    // ── Error UI ────────────────────────────────────────────────────

    _showError(message) {
      if (this.dataset.hideErrors === 'true') return;
      if (!this.errorRegion) {
        this.errorRegion = document.createElement('div');
        this.errorRegion.setAttribute('data-mb-error', '');
        this.errorRegion.setAttribute('role', 'alert');
        this.errorRegion.className = 'mb-pdp-detail__error';
        const anchor =
          this.form.querySelector('.mb-pdp-detail__actions') || this.form;
        anchor.insertAdjacentElement('afterend', this.errorRegion);
      }
      this.errorRegion.textContent =
        message || 'Something went wrong. Please try again.';
      this.errorRegion.hidden = false;
    }

    _clearError() {
      if (this.errorRegion && !this.errorRegion.hidden) {
        this.errorRegion.hidden = true;
        this.errorRegion.textContent = '';
      }
      if (this.dataset.state === 'error') this._setState('idle');
    }

    _onFormMutation() {
      // Reserved for future use (e.g. observing variant-picker re-renders).
    }

    // ── Submit ──────────────────────────────────────────────────────

    _onSubmit(event) {
      event.preventDefault();
      if (this._submitting) return;

      const primary =
        this.form.querySelector('[type=submit][name=add]') ||
        this.form.querySelector('[type=submit]');

      // Hard block when the variant picker has marked the CTA disabled
      // (sold out, or the option combination resolves to no variant).
      if (
        primary &&
        (primary.disabled || primary.getAttribute('aria-disabled') === 'true')
      ) {
        if (primary.disabled) {
          this._showError('This item is currently sold out.');
        }
        return;
      }

      this._submitting = true;
      this._setState('loading');
      this._clearError();

      const variantId = this.variantInput && this.variantInput.value;
      const sectionId = this.dataset.sectionId || null;

      this.dispatchEvent(
        new CustomEvent('product-form:loading', {
          bubbles: true,
          composed: true,
          detail: { variantId, sectionId },
        })
      );

      // Build the main item from form inputs. Shopify /cart/add.js JSON
      // payload uses items[] objects, NOT form-encoded fields, so we
      // collect what we need explicitly: id (required), quantity, any
      // line-item properties[X], and an optional selling_plan.
      const mainItem = this._buildItemFromForm(variantId);

      // Concatenate sibling bundle items (selected rug pad + addons).
      // Lookup is scoped to the variant-picker wrapper first, then falls
      // back to document so older layouts still find the bundle.
      const bundleEl =
        this.closest('mb-variant-picker')?.querySelector('product-bundle') ||
        document.querySelector('product-bundle');
      const bundleItems =
        bundleEl && typeof bundleEl.getSelectedItems === 'function'
          ? bundleEl.getSelectedItems()
          : [];
      const items = [mainItem, ...bundleItems];

      // Tell Shopify which sections to render in the response — saves a
      // follow-up GET. Use the cart element's own list when available so
      // we stay aligned with whatever drawer/notification is on the page.
      let sections = this.dataset.cartSections;
      if (!sections && this.cart && typeof this.cart.getSectionsToRender === 'function') {
        sections = this.cart.getSectionsToRender().map((s) => s.id).join(',');
      }
      if (!sections) sections = DEFAULT_SECTIONS;

      // Dawn's cart-drawer uses this to restore focus when it opens.
      if (this.cart && typeof this.cart.setActiveElement === 'function') {
        this.cart.setActiveElement(document.activeElement);
      }

      const url =
        (window.routes && window.routes.cart_add_url
          ? window.routes.cart_add_url
          : '/cart/add') + '.js';

      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((res) => res.text().then((text) => ({ status: res.status, text })))
        .then(({ status, text }) => {
          let data = null;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (_) {
            /* non-JSON response */
          }

          // Shopify uses `status` field in the body to flag errors even
          // on 200s; status >= 400 also counts.
          const errored = status >= 400 || (data && data.status);
          if (errored) {
            const msg =
              (data && (data.description || data.message)) ||
              'Add to cart failed.';
            this._showError(msg);
            this._setState('error');
            this._publish('cartError', {
              source: 'product-form',
              productVariantId: variantId,
              errors: (data && data.errors) || (data && data.description),
              message: msg,
            });
            this.dispatchEvent(
              new CustomEvent('product-form:error', {
                bubbles: true,
                composed: true,
                detail: {
                  variantId,
                  sectionId,
                  message: msg,
                  errors: data && data.errors,
                  response: data,
                },
              })
            );
            return;
          }

          this._setState('added');
          this._publish('cartUpdate', {
            source: 'product-form',
            productVariantId: variantId,
            cartData: data,
          });
          this.dispatchEvent(
            new CustomEvent('product-form:added', {
              bubbles: true,
              composed: true,
              detail: { variantId, sectionId, item: data, response: data },
            })
          );

          this._renderCart(data);

          // Reset the qty stepper back to 1 so subsequent clicks don't
          // keep adding the previous quantity. Both the visible and the
          // hidden inputs are updated.
          const qtyDisplay = this.querySelector('[data-mb-qty-display]');
          const qtyHidden = this.form.querySelector('[data-mb-qty-hidden]');
          if (qtyDisplay) qtyDisplay.value = '1';
          if (qtyHidden) qtyHidden.value = '1';

          // Schedule the return to idle so the label snaps back to
          // "Add to cart" (or "Sold out" if the variant just sold the
          // last unit in this very transaction).
          if (this._successTimer) clearTimeout(this._successTimer);
          const dur = parseInt(this.dataset.successDuration, 10);
          this._successTimer = setTimeout(
            () => this._setState('idle'),
            Number.isFinite(dur) ? dur : SUCCESS_DURATION_MS
          );
        })
        .catch((err) => {
          this._showError('Network error. Please try again.');
          this._setState('error');
          this.dispatchEvent(
            new CustomEvent('product-form:error', {
              bubbles: true,
              composed: true,
              detail: {
                variantId,
                sectionId,
                message: (err && err.message) || 'Network error',
                errors: null,
              },
            })
          );
        })
        .finally(() => {
          this._submitting = false;
        });
    }

    /**
     * Apply the sections returned by /cart/add.js. Preference order:
     *   1) Dawn cart-drawer.renderContents()  — opens the drawer too
     *   2) Inline DOM swap for any section in the response
     *
     * Dawn's cart-drawer ships with an `.is-empty` class on the host
     * element whenever Liquid renders with `cart == empty`. CSS hides
     * `.cart__contents` and footer when that class is up the tree
     * (component-cart.css). renderContents() only swaps the inner
     * `#CartDrawer` markup, so without explicitly stripping `is-empty`
     * here the drawer stays in its "your cart is empty" visual state
     * even though the items just landed in the DOM.
     */
    _renderCart(response) {
      const drawer =
        this.cart ||
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification');

      if (drawer) drawer.classList.remove('is-empty');

      if (drawer && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(response);
          // Dawn's renderContents copies innerHTML over the live
          // #cart-icon-bubble anchor but never touches its
          // aria-label — leaving AT users on "Cart, 0 items" after
          // adding to a previously-empty cart. Sync it here from the
          // fresh section payload.
          this._syncBubbleAriaLabel(response);
          return;
        } catch (_) {
          /* fall through to inline render */
        }
      }

      if (!response || !response.sections) return;
      Object.entries(response.sections).forEach(([id, html]) => {
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh =
          parsed.getElementById('shopify-section-' + id) ||
          parsed.getElementById(id) ||
          parsed.body.firstElementChild;
        const target =
          document.getElementById('shopify-section-' + id) ||
          document.getElementById(id);
        if (fresh && target) target.innerHTML = fresh.innerHTML;
      });

      if (drawer && typeof drawer.open === 'function') drawer.open();
    }

    _syncBubbleAriaLabel(response) {
      const html = response && response.sections && response.sections['cart-icon-bubble'];
      if (!html) return;
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      const fresh = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('#cart-icon-bubble');
      if (!fresh) return;
      const al = fresh.getAttribute('aria-label');
      if (al) bubble.setAttribute('aria-label', al);
    }

    /** Mirror to Dawn's pub-sub bus when it exists. */
    _publish(eventKey, payload) {
      try {
        if (
          typeof window.publish === 'function' &&
          window.PUB_SUB_EVENTS &&
          window.PUB_SUB_EVENTS[eventKey]
        ) {
          window.publish(window.PUB_SUB_EVENTS[eventKey], payload);
        }
      } catch (_) {
        /* never let pub-sub break the happy path */
      }
    }
  }

  customElements.define('product-form', ProductForm);
})();
